import { message } from "antd";
import { Category } from "../services/category/index1";
import { localErrorHandler } from "../utils/errorHandler";

const namespace = "category";

const initialState = {
  list: [],
  pagination: {
    total: 0,
  },
  filters: {},
  currentCategory: null,
  loading: {
    fetch: false,
    add: false,
    update: false,
    delete: false,
    fetchById: false,
    updatePosition: false,
    updateStatusActivate: false,
    updateStatusDeactivate: false,
  },
};

export const fetchCategories = (params) => ({
  type: `${namespace}/fetchCategories`,
  payload: params,
});

export const addCategory = (data) => ({
  type: `${namespace}/addCategory`,
  payload: data,
});

export const updateCategory = (categoryId, data) => ({
  type: `${namespace}/updateCategory`,
  payload: { categoryId, data },
});

export const deleteCategory = (categoryId) => ({
  type: `${namespace}/deleteCategory`,
  payload: categoryId,
});

export const fetchCategoryById = (categoryId) => ({
  type: `${namespace}/fetchCategoryById`,
  payload: categoryId,
});

export const updateCategoryPosition = (categoryId, data) => ({
  type: `${namespace}/updateCategoryPosition`,
  payload: { categoryId, data },
});

export const updateCategoryStatusActivate = (categoryId) => ({
  type: `${namespace}/updateCategoryStatusActivate`,
  payload: { categoryId },
});

export const updateCategoryStatusDeActivate = (categoryId) => ({
  type: `${namespace}/updateCategoryStatusDeActivate`,
  payload: categoryId,
});

export default {
  namespace,
  state: initialState,
  effects: {
    *fetchCategories({ payload }, { call, put, select }) {
      yield put({ type: "startLoading", loadingType: "fetch" });
      const currentFilters = yield select((state) => state[namespace].filters);
      const shopId = yield select((state) => state.shop.data.id);
      const filtersToUse = payload || currentFilters;

      try {
        const response = yield call(Category.getCategories, {
          ...filtersToUse,
          shopId,
        });
        yield put({
          type: "setCategories",
          payload: {
            list: response.data.list || [],
            pagination: {
              total: response.data.total || 0,
            },
            filters: filtersToUse,
          },
        });
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "fetch" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "fetch" });
      }
    },

    *addCategory({ payload }, { call, put }) {
      yield put({ type: "startLoading", loadingType: "add" });
      try {
        yield call(Category.addCategory, payload);
        message.success("Category added successfully");
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "add" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "add" });
      }
    },

    *updateCategory({ payload }, { call, put }) {
      const { categoryId, data } = payload;
      yield put({ type: "startLoading", loadingType: "update" });
      try {
        const response = yield call(Category.updateCategory, data, categoryId);
        message.success("Category updated successfully");

        if (response && response.data) {
          yield put({
            type: "updateCategoryInList",
            payload: { categoryId: categoryId, data: response.data },
          });
        }
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "update" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "update" });
      }
    },

    *deleteCategory({ payload }, { call, put }) {
      yield put({ type: "startLoading", loadingType: "delete" });
      try {
        yield call(Category.deleteCategory, payload);
        yield put({ type: "fetchCategories" });
        message.success("Category deleted successfully");
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "delete" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "delete" });
      }
    },

    *fetchCategoryById({ payload }, { call, put }) {
      yield put({ type: "startLoading", loadingType: "fetchById" });

      try {
        const response = yield call(Category.getCategoryById, payload);
        yield put({ type: "setCurrentCategory", payload: response.data });
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "fetchById" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "fetchById" });
      }
    },

    *updateCategoryPosition({ payload }, { call, put }) {
      const { categoryId, data } = payload;
      yield put({ type: "startLoading", loadingType: "updatePosition" });
      try {
        const response = yield call(
          Category.updateCategoryPosition,
          categoryId,
          data
        );
        message.success("Category position updated successfully");
        if (response && response.data) {
          yield put({
            type: "updateCategoryList",
            payload: { categoryId: categoryId, data: response.data },
          });
        }
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "updatePosition" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "updatePosition" });
      }
    },

    *updateCategoryStatusActivate({ payload }, { call, put }) {
      const { categoryId, data } = payload;
      yield put({ type: "startLoading", loadingType: "updateStatusActivate" });
      try {
        const response = yield call(
          Category.updateCategoryStatusActivate,
          categoryId
        );
        message.success("Category activated successfully");

        if (response && response.data) {
          yield put({
            type: "updateCategoryInList",
            payload: { categoryId: categoryId, data: response.data },
          });

          yield put({
            type: "updateCurrentCategoryIfMatches",
            payload: { categoryId: categoryId, data: response.data },
          });
        } else {
          yield put({ type: "fetchCategories" });
        }
      } catch (error) {
        localErrorHandler({
          namespace,
          error,
          stopLoading: "updateStatusActivate",
        });
      } finally {
        yield put({ type: "stopLoading", loadingType: "updateStatusActivate" });
      }
    },

    *updateCategoryStatusDeActivate({ payload }, { call, put }) {
      yield put({
        type: "startLoading",
        loadingType: "updateStatusDeactivate",
      });
      try {
        const response = yield call(
          Category.updateCategoryStatusDeActivate,
          payload
        );
        message.success("Category deactivated successfully");

        if (response && response.data) {
          yield put({
            type: "updateCategoryInList",
            payload: { categoryId: payload, data: response.data },
          });

          yield put({
            type: "updateCurrentCategoryIfMatches",
            payload: { categoryId: payload, data: response.data },
          });
        } else {
          yield put({ type: "fetchCategories" });
        }
      } catch (error) {
        localErrorHandler({
          namespace,
          error,
          stopLoading: "updateStatusDeactivate",
        });
      } finally {
        yield put({
          type: "stopLoading",
          loadingType: "updateStatusDeactivate",
        });
      }
    },
  },

  reducers: {
    setCategories(state, { payload }) {
      return {
        ...state,
        list: payload.list,
        pagination: payload.pagination,
        filters: payload.filters,
      };
    },
    setCurrentCategory(state, { payload }) {
      return {
        ...state,
        currentCategory: payload,
      };
    },

    deleteCategoryFromList(state, { payload }) {
      if (!payload) return state;
      const categoryList = [...state.list];
      const index = categoryList.findIndex(
        (category) => category.id === payload
      );
      if (index !== -1) {
        categoryList.splice(index, 1);
        return {
          ...state,
          list: categoryList,
        };
      }

      return state;
    },
    clearCurrentCategoryIfMatches(state, { payload }) {
      if (state.currentCategory && state.currentCategory.id === payload) {
        return {
          ...state,
          currentCategory: null,
        };
      }
      return state;
    },
    updateCurrentCategoryIfMatches(state, { payload }) {
      if (
        state.currentCategory &&
        state.currentCategory.id === payload.categoryId
      ) {
        return {
          ...state,
          currentCategory: payload.data,
        };
      }
      return state;
    },
    updateCategoryInList(state, { payload }) {
      const { categoryId, data } = payload;
      const categoryList = [...state.list];
      const index = categoryList.findIndex(
        (category) => category.id === categoryId
      );
      if (index !== -1) {
        categoryList[index] = data;
        return {
          ...state,
          list: categoryList,
        };
      }

      return state;
    },

    updateCategoryList(state, { payload }) {
      const { data } = payload;
      return {
        ...state,
        list: [...data],
      };
    },

    startLoading(state, { loadingType }) {
      return {
        ...state,
        loading: { ...state.loading, [loadingType]: true },
      };
    },
    stopLoading(state, { loadingType }) {
      return {
        ...state,
        loading: { ...state.loading, [loadingType]: false },
      };
    },
  },
};
